import { useTranslation } from 'next-i18next'
import { tm } from '@captaincause/ui/tm'
import { ProjectCard } from '@captaincause/ui/ProjectCard'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { Button } from 'components/Button'
import { GiveProjectsGrid } from 'components/GiveProjectsGrid'
import { PrivateProjectCard } from './PrivateProjectCard'
import type { Project } from '@captaincause/types'

interface ProjectsListProps {
  projects: Project[]
  clearFilters?: () => void
  withPrivateCardCTA?: boolean
}

export function ProjectsList({
  projects,
  clearFilters = undefined,
  withPrivateCardCTA = true,
}: ProjectsListProps) {
  const { t } = useTranslation('projects.page')

  const locale = useNextLocale()

  const clear = () => {
    clearFilters?.()
  }

  const emergencyProjects = projects
    .filter(project => project.is_emergency)
    .slice(0, 6)
  const otherProjects = projects.filter(project => !project.is_emergency)

  return (
    <div>
      {emergencyProjects.length > 0 ? (
        <div className="flex flex-col items-center border-b border-inside px-4 pt-6 md:items-start md:px-0">
          <span className="text-sm font-semibold text-dark-gray">
            {t('featured')}
          </span>
          <div className="mt-1 text-center text-2xl font-semibold">
            {/* TODO: Have this provided by the backend */}
            {t('urgent')}
          </div>
          <GiveProjectsGrid size={emergencyProjects.length} className="pb-0">
            {emergencyProjects.map(project => (
              <ProjectCard
                key={project.name}
                project={project}
                hasPriorityImage
                promoted={!!project.is_emergency}
                locale={locale}
                Link={Button}
                variant="catalog"
              />
            ))}
          </GiveProjectsGrid>
        </div>
      ) : null}
      <div
        className={tm(
          'mx-auto grid h-fit grid-cols-1 gap-x-10 gap-y-8 p-10 px-4 sm:grid-cols-2 md:grid-cols-2 md:px-0 lg:grid-cols-3 xl:grid-cols-4',
          otherProjects.length === 0 &&
            'sm:grid-cols-1 md:grid-cols-1  lg:grid-cols-1 xl:grid-cols-1',
          otherProjects.length <= 3 &&
            otherProjects.length !== 0 &&
            'lg:grid-cols-3 xl:grid-cols-3',
        )}
      >
        {otherProjects.length > 0 && withPrivateCardCTA ? (
          <PrivateProjectCard />
        ) : null}
        {otherProjects.length > 0 ? (
          otherProjects.map((project, index) => (
            <ProjectCard
              key={project.name}
              project={project}
              hasPriorityImage={index < 4}
              locale={locale}
              Link={Button}
              promoted={!!project.is_emergency}
              variant="catalog"
              style={{
                order: index >= 1 && withPrivateCardCTA ? index + 1 : index,
              }}
            />
          ))
        ) : (
          <div className="my-12 flex flex-col items-center md:m-auto">
            <div className="text-center text-lg">
              {t('search.not-found.title')}
            </div>
            <div className="text-base">{t('search.not-found.retry')} </div>
            <Button variant="secondary" small onClick={clear} className="mt-4">
              {t('search.not-found.reset')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
